import _ from 'lodash';
import { FilterMethod, FilterType, ICustomFilter, IFilterSchema } from './filters.models';

export function getFilterMethods(
    filterType: FilterType,
): {
    value: FilterMethod;
    label: string;
}[] {
    switch (filterType) {
        /**
         * EQ and LK only, and the omissions are the point.
         *
         * A multi-value filter is sent as one comma-joined value (`EQ:a,EQ:b`) which the API treats
         * as OR. That is what "is any of" means, and it holds for LK too — both measured against
         * sandbox on `GET /provider/claim`: EQ pair → 2 of 971, LK pair → 2 of 971.
         *
         * NQ is deliberately absent. OR-ing negations gives NOT(a) OR NOT(b), which is every row:
         * `NQ:a,NQ:b` measured **971 of 971** — a filter that looks applied, reads as "is none of",
         * and returns the whole table. NLK has the same shape and is excluded for the same reason.
         * Do not add either without re-measuring (NS-3).
         *
         * EMPTY/NEMPTY are meaningless for a pasted list.
         */
        case 'MultiString':
            return [
                {
                    value: FilterMethod.EQ,
                    label: 'Is any of',
                },
                {
                    value: FilterMethod.LK,
                    label: 'Contains any of',
                },
            ];
        case 'Phone':
        case 'String':
            return [
                {
                    value: FilterMethod.EQ,
                    label: 'Equal',
                },
                {
                    value: FilterMethod.NQ,
                    label: 'Not equal',
                },
                {
                    value: FilterMethod.LK,
                    label: 'Like',
                },
                {
                    value: FilterMethod.NLK,
                    label: 'Not like',
                },
                {
                    value: FilterMethod.EMPTY,
                    label: 'Empty',
                },
                {
                    value: FilterMethod.NEMPTY,
                    label: 'Not Empty',
                },
            ];
        case 'FullText':
            return [
                {
                    value: FilterMethod.OW,
                    label: 'One of the words',
                },
                {
                    value: FilterMethod.CT,
                    label: 'Contains',
                },
                {
                    value: FilterMethod.NCT,
                    label: 'Not contains',
                },
                {
                    value: FilterMethod.EX,
                    label: 'Exact',
                },
                {
                    value: FilterMethod.FT,
                    label: 'FullText search',
                },
            ];
        case 'Date':
        case 'DateTime':
            return [
                {
                    value: FilterMethod.EQ,
                    label: 'On',
                },
                {
                    value: FilterMethod.NQ,
                    label: 'Not on',
                },
                {
                    value: FilterMethod.GT,
                    label: 'After',
                },
                {
                    value: FilterMethod.LT,
                    label: 'Before',
                },
                {
                    value: FilterMethod.GTE,
                    label: 'On or after',
                },
                {
                    value: FilterMethod.LTE,
                    label: 'On or before',
                },
                {
                    value: FilterMethod.EMPTY,
                    label: 'Empty',
                },
                {
                    value: FilterMethod.NEMPTY,
                    label: 'Not Empty',
                },
            ];
        case 'Number':
            return [
                {
                    value: FilterMethod.EQ,
                    label: 'Equal',
                },
                {
                    value: FilterMethod.NQ,
                    label: 'Not equal',
                },
                {
                    value: FilterMethod.GT,
                    label: 'Greater than',
                },
                {
                    value: FilterMethod.LT,
                    label: 'Less than',
                },
                {
                    value: FilterMethod.GTE,
                    label: 'Greater than or equal to',
                },
                {
                    value: FilterMethod.LTE,
                    label: 'Less than or equal to',
                },
                {
                    value: FilterMethod.EMPTY,
                    label: 'Empty',
                },
                {
                    value: FilterMethod.NEMPTY,
                    label: 'Not Empty',
                },
            ];
        case 'Boolean':
        case 'AsyncSelect':
        case 'Select':
            return [
                {
                    value: FilterMethod.EQ,
                    label: 'Equal',
                },
                {
                    value: FilterMethod.NQ,
                    label: 'Not equal',
                },
                {
                    value: FilterMethod.EMPTY,
                    label: 'Empty',
                },
                {
                    value: FilterMethod.NEMPTY,
                    label: 'Not Empty',
                },
            ];
        default:
            throw new Error(`Unknown filter type ${filterType}`);
    }
}

/**
 * The pasted text of a `MultiString` filter, split into the values actually sent.
 *
 * Accepts whatever a paste realistically contains — newlines, commas, semicolons, tabs, stray
 * spaces — because the list comes out of a spreadsheet or another console as often as it is typed.
 * Blanks are dropped and duplicates collapsed, so `a,,a\nb` is two conditions, not four.
 *
 * Single source of truth: the request builder and the active-filter chip both call this, so what
 * the chip counts is exactly what the request asks for.
 */
export function splitMultiValues(value: string | undefined): string[] {
    if (!value) {
        return [];
    }

    return _.uniq(
        value
            .split(/[\s,;]+/)
            .map(part => part.trim())
            .filter(Boolean),
    );
}

/**
 * The methods a MultiString filter may be SENT with — derived from the ones it OFFERS, so the menu
 * and the gate below cannot drift apart. Anything else, above all NQ/NLK, ORs into "every row".
 */
export const MULTI_STRING_METHODS: FilterMethod[] = getFilterMethods('MultiString').map(m => m.value);

/**
 * The most values one MultiString filter may carry.
 *
 * Not a product rule — a transport one. The values travel in the query string, and CloudFront
 * refuses a URL over 8,192 bytes with a 414 before the API ever sees it. Measured on sandbox
 * `GET /provider/claim` with the params the Claims page sends: 182 claim UUIDs → 8,139 bytes, 200;
 * 184 → 8,227 bytes, 414. A UUID costs ~44 bytes once URL-encoded (`EQ%3A` + 36 + `%2C`), so 150
 * leaves ~1.4 KB for every other filter on the same request.
 *
 * Chunking into several requests is not the way past this: separate requests cannot share one
 * server-side page, sort order or total, so the table would stop being a filtered table at all.
 */
export const MULTI_STRING_MAX_VALUES = 150;

/**
 * Whether a filter counts as applied — the single gate for BOTH the request and the active chip.
 *
 * For every type but MultiString this is the long-standing rule, unchanged. A MultiString filter
 * additionally needs an OR-safe method and 1..MULTI_STRING_MAX_VALUES values, because each way of
 * missing those used to leave a chip on screen that the table did not honour:
 *  - a method it does not offer — kept from another field, or restored from storage — sent
 *    `NQ:a,NQ:b` and returned the whole table under a chip reading "any of N values";
 *  - an empty box, or one holding only separators, read "any of 0 values" over an unfiltered table;
 *  - too many values failed the whole listing with CloudFront's 414.
 */
export function isFilterValid(filter: ICustomFilter): boolean {
    if (_.isUndefined(filter.filterId) || _.isUndefined(filter.filterMethod)) {
        return false;
    }

    if (filter.filterType === 'MultiString') {
        const count = splitMultiValues(filter.value).length;

        return MULTI_STRING_METHODS.includes(filter.filterMethod) && count > 0 && count <= MULTI_STRING_MAX_VALUES;
    }

    return !_.isUndefined(filter.value) || isEmptyValueMethod(filter.filterMethod);
}

/**
 * The method a filter keeps when its FIELD changes: the current one if the new field offers it,
 * otherwise none, so the method select prompts instead of carrying a choice it cannot display.
 *
 * Keeping the old method unconditionally is how "Payer · Not equal" switched to ID still sent NQ
 * — with the method select showing blank, because MultiString does not list NQ.
 */
export function methodForField(method: FilterMethod | undefined, filterType: FilterType): FilterMethod | undefined {
    if (!method) {
        return method;
    }

    return getFilterMethods(filterType).some(option => option.value === method) ? method : undefined;
}

/**
 * Stored filters, brought in line with the fields they now belong to.
 *
 * `TableStorage` keeps filters across deploys, so one saved before a field became MultiString comes
 * back typed `String` — still sent, while the panel renders MultiString controls that cannot show its
 * method. A saved "ID · Not equal x" reloaded as **ID · (blank) · x** under a chip reading
 * `Filter Id: "x"`, over a table showing every claim EXCEPT x. A false statement on screen (NS-3).
 *
 * So a filter whose field is now MultiString is retyped to it, and keeps its method only if
 * MultiString offers it (`methodForField`). EQ and LK carry on exactly as before — same request, same
 * chip. NQ, NLK, EMPTY and NEMPTY lose the method, which leaves the filter unapplied and chip-less
 * with the method select prompting — honest, where keeping them was not.
 *
 * Deliberately NOT called from the DataTablePresenter constructor: that runs for every table in both
 * apps (graph: 253 direct constructors, CRITICAL), and only a page whose schema adopted MultiString
 * has anything to align. Call it where that page loads its stored filters. Filters on other fields are
 * returned as the same objects, untouched.
 */
export function alignFiltersWithSchemas(filters: ICustomFilter[], schemas: IFilterSchema[]): ICustomFilter[] {
    return filters.map(filter => {
        const schema = schemas.find(s => s.filterId === filter.filterId);
        if (!schema || schema.filterType !== 'MultiString') {
            return filter;
        }

        return {
            ...filter,
            filterType: 'MultiString',
            filterMethod: methodForField(filter.filterMethod, 'MultiString'),
        } as ICustomFilter;
    });
}

/**
 * The active-filter chip text for a MultiString filter, after its label.
 *
 * Several values carry a count, since a pasted list does not fit in a chip. One value is spelled out —
 * and says "contains" for LK, because a single fragment otherwise read exactly like an exact match
 * (`Filter Id: "005ff"`), while the multi-value chip already distinguished them.
 */
export function multiStringChipText(method: FilterMethod | undefined, value: string | undefined): string {
    const values = splitMultiValues(value);
    const contains = method === FilterMethod.LK;

    if (values.length === 1) {
        return contains ? ` contains "${values[0]}"` : `: "${values[0]}"`;
    }

    return ` ${contains ? 'containing any of' : 'any of'} ${values.length} values`;
}

export function isEmptyValueMethod(method: FilterMethod | undefined) {
    if (!method) {
        return false;
    }

    const emptyMethods = [FilterMethod.EMPTY, FilterMethod.NEMPTY];

    return emptyMethods.includes(method);
}

export function parseFilterFirstLast(value: string): [string | undefined, string | undefined] {
    const match = value.match(/([a-zA-Z]{2,})+/gi);

    if (!match) {
        return [undefined, undefined];
    }

    return [match[0], match[1]];
}

export function parseFilterNumber(value: string): string | undefined {
    const match = value.match(/\d{2,}/gi);

    return match ? match[0] : undefined;
}

// date in ISO
export function parseFilterDate(value: string): Date | undefined {
    // 02/02/2021
    const match = value.match(/(0[1-9]|1[012])[- \.\/](0[1-9]|[12][0-9]|3[01])[- \.\/](19|20)\d{2}/gi);

    if (!match || !match[0]) {
        return;
    }

    return new Date(`${match[0]} UTC`);
}
